import {
  Evaluation,
  EvaluationCategory,
  EvaluationCategoryRival,
  EvaluationExhibition,
  EvaluationProduct,
  EvaluationSubCategory,
} from '../../models/interfaces/evaluation';
import {
  EvaluationCategoryPayload,
  EvaluationCategoryRivalPayload,
  EvaluationExhibitionPayload,
  EvaluationPayload,
  EvaluationPhotographicEvidencePayload,
  EvaluationProductPayload,
  EvaluationSubCategoryPayload,
} from '../../models/interfaces/evaluation-payload.interface';

export interface ValidationContext {
  kind: 'field';
}

export class EvaluationValidationError extends Error {
  constructor(
    public readonly fieldPath: string,
    public readonly context?: ValidationContext,
  ) {
    super(`Campo inválido: ${fieldPath}`);
    this.name = 'EvaluationValidationError';
  }
}

/** Verifica obligatorios y mapeabilidad. Lanza EvaluationValidationError. */
export function validateEvaluation(evaluation: Evaluation): void {
  const required = (value: unknown, fieldPath: string): void => {
    if (value === null || value === undefined || value === '') {
      throw new EvaluationValidationError(fieldPath, { kind: 'field' });
    }
  };

  required(evaluation.instrument_id, 'instrument_id');
  required(evaluation.route_id, 'route_id');
  required(evaluation.customer_id, 'customer_id');
  required(evaluation.establishment_id, 'establishment_id');
  if (!Array.isArray(evaluation.categories)) {
    throw new EvaluationValidationError('categories', { kind: 'field' });
  }

  evaluation.categories.forEach((category, ci) => {
    required(category.product_category_id, `categories[${ci}].product_category_id`);

    (category.segments || []).forEach((segment, si) => {
      required(segment.segment_id, `categories[${ci}].segments[${si}].segment_id`);
    });

    (category.products || []).forEach((product, pi) => {
      required(product.product_id, `categories[${ci}].products[${pi}].product_id`);
      required(product.product_category_id, `categories[${ci}].products[${pi}].product_category_id`);

      (product.expirations || []).forEach((expiration, xi) => {
        if (!hasExpirationData(expiration)) return;
        required(expiration.lot, `categories[${ci}].products[${pi}].expirations[${xi}].lot`);
      });
    });

    (category.rivals || []).forEach((rival, ri) => {
      required(rival.product_category_rival_id, `categories[${ci}].rivals[${ri}].product_category_rival_id`);
    });
  });

  (evaluation.exhibitions || []).forEach((exhibition, ei) => {
    required(exhibition.exhibition_id, `exhibitions[${ei}].exhibition_id`);
  });
}

/** Proyecta el objeto interno a su DTO de envío. No falla. */
export function toEvaluationPayload(evaluation: Evaluation): EvaluationPayload {
  return {
    instrument_id: evaluation.instrument_id,
    route_id: evaluation.route_id,
    customer_id: evaluation.customer_id,
    establishment_id: evaluation.establishment_id,
    latitude: evaluation.latitude,
    longitude: evaluation.longitude,
    created_app_at: evaluation.created_app_at,
    finished_at: evaluation.finished_at,
    send_point: evaluation.send_point ? { ...evaluation.send_point } : undefined,
    end_point: evaluation.end_point ? { ...evaluation.end_point } : undefined,
    evidences: evaluation.evidences?.length ? [...evaluation.evidences] : undefined,
    categories: evaluation.categories.map(toCategoryPayload),
    exhibitions: evaluation.exhibitions?.length
      ? evaluation.exhibitions.map(toExhibitionPayload)
      : undefined,
  };
}

function toCategoryPayload(category: EvaluationCategory): EvaluationCategoryPayload {
  return {
    product_category_id: category.product_category_id,
    category_space: category.category_space,
    category_tot: category.category_tot,
    ...withFaces(category.faces),
    customer_refrigerators: category.customer_refrigerators,
    supplied_boxes: category.supplied_boxes,
    rows: category.rows,
    columns: category.columns,
    collage_files: (category.collageFileIds ?? [])
      .reduce<{ position: number; file_id: number }[]>((acc, fileId, index) => {
        if (fileId !== undefined && fileId !== null) {
          acc.push({ position: index + 1, file_id: fileId });
        }
        return acc;
      }, []),
    segments: category.segments?.map((s) => ({ segment_id: s.segment_id, category_space: s.category_space, ...withFaces(s.faces) })),
    photographic_evidences: category.photographic_evidences?.map(toPhotographicEvidencePayload),
    pops: category.pops?.map((p) => ({ pop_classification_id: p.pop_classification_id, value: p.value })),
    spaces: category.spaces?.map((s) => ({ space_classification_id: s.space_classification_id, value: s.value })),
    products: category.products?.map(toProductPayload),
    rivals: category.rivals?.map(toCategoryRivalPayload),
    sub_categories: category.sub_categories?.map(toSubCategoryPayload),
  };
}

function toPhotographicEvidencePayload(
  evidence: EvaluationCategory['photographic_evidences'][number],
): EvaluationPhotographicEvidencePayload {
  return {
    image_type_id: evidence.image_type_id,
    image_before: evidence.image_before,
    image_after: evidence.image_after,
    products: evidence.products,
    sku: evidence.sku,
    price_tag: evidence.price_tag,
    cenefa_sticker: evidence.cenefa_sticker,
  };
}

function toProductPayload(product: EvaluationProduct): EvaluationProductPayload {
  const expirations = (product.expirations || []).filter(hasExpirationData);

  return {
    product_id: product.product_id,
    product_category_id: product.product_category_id,
    product_sub_category_id: product.product_sub_category_id,
    price: product.price,
    inventory: product.inventory,
    expirations: expirations.length
      ? expirations.map((e) => ({
        lot: e.lot,
        due_date: e.due_date,
        quantity: e.quantity,
        description: e.description,
      }))
      : undefined,
    rivals: product.rivals?.map((r) => ({
      product_rival_id: r.product_rival_id,
      price: r.price,
      inventory: r.inventory,
    })),
  };
}

/** Una fila de vencimiento "vacía" (recién agregada y sin tocar) no se envía. */
function hasExpirationData(expiration: { lot?: string; due_date?: string; quantity?: number; description?: string }): boolean {
  return Boolean(expiration.lot || expiration.due_date || expiration.quantity || expiration.description);
}

/** Incluye `faces` solo si tiene un valor numérico válido; null/vacío/NaN se omiten del payload. */
function withFaces(value: unknown): { faces?: number } {
  if (value === null || value === undefined || value === '') return {};
  const parsed = Number(value);
  return Number.isNaN(parsed) ? {} : { faces: parsed };
}

function toCategoryRivalPayload(rival: EvaluationCategoryRival): EvaluationCategoryRivalPayload {
  return {
    product_category_rival_id: rival.product_category_rival_id,
    category_space: rival.category_space,
    ...withFaces(rival.faces),
    category_tot: rival.category_tot,
    customer_refrigerators: rival.customer_refrigerators,
    pops: rival.pops?.map((p) => ({ pop_classification_id: p.pop_classification_id, value: p.value })),
    spaces: rival.spaces?.map((s) => ({ space_classification_id: s.space_classification_id, value: s.value })),
  };
}

function toSubCategoryPayload(sub: EvaluationSubCategory): EvaluationSubCategoryPayload {
  return {
    product_sub_category_id: sub.product_sub_category_id,
    category_space: sub.category_space,
    category_additional_display: sub.category_additional_display,
    category_pop: sub.category_pop,
    category_tot: sub.category_tot,
    customer_refrigerators: sub.customer_refrigerators,
    rivals: sub.rivals?.map((r) => ({
      product_sub_category_rival_id: r.product_sub_category_rival_id,
      category_space: r.category_space,
      category_additional_display: r.category_additional_display,
      category_pop: r.category_pop,
      category_tot: r.category_tot,
      customer_refrigerators: r.customer_refrigerators,
    })),
  };
}

function toExhibitionPayload(exhibition: EvaluationExhibition): EvaluationExhibitionPayload {
  return {
    exhibition_id: exhibition.exhibition_id,
    customer_brand_id: exhibition.customer_brand_id,
    event_type_id: exhibition.event_type_id,
    file_id: exhibition.file_id,
    details: exhibition.details?.map((d) => ({
      product_category_id: d.product_category_id,
      category_space: d.category_space,
    })),
  };
}
